import axios from "axios";
import * as JSONBig from "json-bigint";
import { TIMEOUT } from "../../../components/common/configuration/ConfigEnvironment";
import { getStoredMerchantPortalToken } from "../auth/merchantAuthSession";
import { pickMerchantField, toMerchantString } from "./merchantLegacyUtils";

const MERCHANT_API_SUCCESS_CODES = [200, "200", 0, "0", "000000"];
// todo 993
const MERCHANT_API_BASE_URL =
  "https://merchant.uniwebpay.com/merchant-management/";
// Test merchant API base URL:
// const MERCHANT_API_BASE_URL = "https://merchant-stage.uniwebpay.com/";
const MERCHANT_PORTAL_BASE_URL = "https://merchant.uniwebpay.com/";
let merchantApiInFlightCount = 0;
const merchantApiActivityListeners = new Set();

function emitMerchantApiActivity() {
  const snapshot = {
    inFlightCount: merchantApiInFlightCount,
    isLoading: merchantApiInFlightCount > 0,
  };

  merchantApiActivityListeners.forEach(function notify(listener) {
    listener(snapshot);
  });
}

function beginMerchantApiRequest() {
  merchantApiInFlightCount += 1;
  emitMerchantApiActivity();
}

function endMerchantApiRequest() {
  merchantApiInFlightCount = Math.max(0, merchantApiInFlightCount - 1);
  emitMerchantApiActivity();
}

const merchantApiClient = axios.create({
  baseURL: MERCHANT_API_BASE_URL,
  timeout: TIMEOUT,
  transformResponse: function transformResponse(data) {
    try {
      return JSONBig.parse(data);
    } catch (error) {
      return data;
    }
  },
});

merchantApiClient.interceptors.request.use(function applyMerchantToken(config) {
  beginMerchantApiRequest();
  const token = getStoredMerchantPortalToken();

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = "Bearer " + token;
  }

  return config;
},
function handleMerchantRequestError(error) {
  endMerchantApiRequest();

  return Promise.reject(error);
});

merchantApiClient.interceptors.response.use(
  function unwrapMerchantResponse(response) {
    endMerchantApiRequest();
    return response.data;
  },
  function rejectMerchantResponse(error) {
    endMerchantApiRequest();
    return Promise.reject(error);
  },
);

function sanitizeParams(params) {
  return Object.keys(params || {}).reduce(function buildParams(result, key) {
    const value = params[key];

    if (value !== "" && value !== null && value !== undefined) {
      result[key] = value;
    }

    return result;
  }, {});
}

function isSuccessCode(code) {
  return MERCHANT_API_SUCCESS_CODES.indexOf(code) >= 0;
}

function createMerchantApiError(message, response, originalError) {
  const error = new Error(message || "Merchant API request failed");

  error.response = response;
  error.originalError = originalError;

  return error;
}

function unwrapMerchantApiData(response) {
  if (!response || typeof response !== "object") {
    throw createMerchantApiError("Merchant API returned an empty response", response);
  }

  if (
    response.code !== undefined &&
    response.code !== null &&
    !isSuccessCode(response.code)
  ) {
    throw createMerchantApiError(response.message || "Merchant API request failed", response);
  }

  return response.data;
}

async function requestMerchantApi(config) {
  const response = await merchantApiClient(config);

  return unwrapMerchantApiData(response);
}

async function requestMerchantRaw(config) {
  beginMerchantApiRequest();

  try {
    const token = getStoredMerchantPortalToken();
    const response = await axios({
      baseURL: MERCHANT_API_BASE_URL,
      timeout: TIMEOUT,
      ...config,
      headers: {
        ...(config && config.headers ? config.headers : {}),
        ...(token ? { Authorization: "Bearer " + token } : {}),
      },
    });

    return response;
  } catch (error) {
    throw error;
  } finally {
    endMerchantApiRequest();
  }
}

export function resolveMerchantPortalUrl(path) {
  const normalizedPath = toMerchantString(path, "").trim();

  if (!normalizedPath) {
    return "";
  }

  if (/^https?:\/\//i.test(normalizedPath)) {
    return normalizedPath;
  }

  return (
    MERCHANT_PORTAL_BASE_URL.replace(/\/+$/, "") +
    "/" +
    normalizedPath.replace(/^\/+/, "")
  );
}

function getMerchantResponseFilename(headers, fallbackFilename) {
  const contentDisposition =
    (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || "";

  if (!contentDisposition) {
    return fallbackFilename;
  }

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (encodedMatch && encodedMatch[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].replace(/["']/g, ""));
    } catch (error) {
      return encodedMatch[1].replace(/["']/g, "");
    }
  }

  const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

  return plainMatch && plainMatch[1] ? plainMatch[1] : fallbackFilename;
}

function buildMerchantExportFilename(prefix, extension) {
  return prefix + "-" + new Date().toISOString().slice(0, 10) + "." + extension;
}

export function getMerchantApiActivitySnapshot() {
  return {
    inFlightCount: merchantApiInFlightCount,
    isLoading: merchantApiInFlightCount > 0,
  };
}

export function subscribeMerchantApiActivity(listener) {
  merchantApiActivityListeners.add(listener);

  return function unsubscribeMerchantApiActivity() {
    merchantApiActivityListeners.delete(listener);
  };
}

export function getMerchantApiErrorMessage(error, fallbackMessage) {
  return toMerchantString(
    pickMerchantField(error, [
      "message",
    ]),
    toMerchantString(
      pickMerchantField(error && error.response, ["message"]),
      fallbackMessage || "Merchant API request failed",
    ),
  );
}

export function isMerchantUnauthorizedError(error) {
  const responseStatus =
    error &&
    error.originalError &&
    error.originalError.response &&
    error.originalError.response.status;
  const directStatus = error && error.response && error.response.status;
  const code = error && error.response && error.response.code;

  return (
    responseStatus === 401 ||
    responseStatus === 403 ||
    directStatus === 401 ||
    directStatus === 403 ||
    code === 401 ||
    code === 403
  );
}

export async function merchantLogin(credentials) {
  return requestMerchantApi({
    url: "merchant/auth/login",
    method: "post",
    data: credentials,
    headers: {
      "Content-Type": "application/json",
    },
  });
}

export async function fetchMerchantAuthMe() {
  return requestMerchantApi({
    url: "merchant/auth/me",
    method: "get",
  });
}

export async function fetchMerchantContext() {
  return requestMerchantApi({
    url: "merchant/context",
    method: "get",
  });
}

export async function fetchMerchantDashboardSummary(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/summary",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantDashboardRecentTransactions(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/recent-transactions",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantDashboardTopOutlets(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/top-outlets",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantDashboardPaymentMethods(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/payment-methods",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantDashboardCardCountries(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/card-issuing-countries",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantDashboardSalesTrend(params) {
  return requestMerchantApi({
    url: "merchant/dashboard/sales-trend",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantOutlets(params) {
  return requestMerchantApi({
    url: "merchant/outlets",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantOutletGmvDistribution(params) {
  return requestMerchantApi({
    url: "merchant/outlets/gmv-distribution",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantTransactions(params) {
  return requestMerchantApi({
    url: "merchant/transactions",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantSettlements(params) {
  return requestMerchantApi({
    url: "merchant/settlements",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function fetchMerchantSettlementReport(summaryId) {
  return requestMerchantApi({
    url: "merchant/settlements/" + encodeURIComponent(summaryId) + "/report",
    method: "get",
  });
}

export async function fetchMerchantTransactionFilters(params) {
  return requestMerchantApi({
    url: "merchant/transactions/filters",
    method: "get",
    params: sanitizeParams(params),
  });
}

export async function exportMerchantTransactions(params) {
  const response = await requestMerchantRaw({
    url: "merchant/transactions/export",
    method: "get",
    params: sanitizeParams(params),
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename: getMerchantResponseFilename(
      response.headers,
      buildMerchantExportFilename("merchant-transactions", "csv"),
    ),
  };
}

export function downloadMerchantBlob(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

export async function fetchMerchantTransactionDetail(transactionId) {
  return requestMerchantApi({
    url: "merchant/transactions/" + encodeURIComponent(transactionId),
    method: "get",
  });
}

export function normalizeMerchantApiTransaction(record) {
  const storeObject =
    record && record.store && typeof record.store === "object" ? record.store : null;
  const storeId = toMerchantString(
    pickMerchantField(record, ["store_id"]),
    toMerchantString(pickMerchantField(storeObject, ["id"]), ""),
  );
  const storeName = toMerchantString(
    pickMerchantField(record, ["store_name"]),
    toMerchantString(
      pickMerchantField(storeObject, ["merchant_name", "name"]),
      storeId ? "Outlet " + storeId : "Unknown Store",
    ),
  );
  const createdAt = toMerchantString(pickMerchantField(record, ["created_at"]), "");
  const issuerCountry = toMerchantString(
    pickMerchantField(record, ["issuer_country", "card_belong"]),
    "",
  );

  return {
    ...record,
    id: toMerchantString(pickMerchantField(record, ["txn_id", "id"]), ""),
    txnId: toMerchantString(pickMerchantField(record, ["txn_id", "id"]), ""),
    storeId: storeId,
    storeName: storeName,
    store: storeName,
    amount: pickMerchantField(record, ["amount"], 0),
    currency: toMerchantString(pickMerchantField(record, ["currency"]), "SGD"),
    paymentMethod: toMerchantString(
      pickMerchantField(record, ["payment_method", "dcs_payment_method"]),
      "Payment",
    ),
    payment_method: toMerchantString(
      pickMerchantField(record, ["payment_method", "dcs_payment_method"]),
      "Payment",
    ),
    dcsPaymentMethod: toMerchantString(
      pickMerchantField(record, ["dcs_payment_method", "payment_method"]),
      "",
    ),
    dcs_payment_method: toMerchantString(
      pickMerchantField(record, ["dcs_payment_method", "payment_method"]),
      "",
    ),
    status: toMerchantString(pickMerchantField(record, ["status"]), ""),
    paymentStatus: toMerchantString(pickMerchantField(record, ["status"]), ""),
    refundStatus: toMerchantString(pickMerchantField(record, ["refund_status"]), ""),
    refund_status: toMerchantString(pickMerchantField(record, ["refund_status"]), ""),
    scheme: toMerchantString(pickMerchantField(record, ["scheme", "card_pay_way"]), ""),
    cardPayWay: toMerchantString(
      pickMerchantField(record, ["card_pay_way", "scheme"]),
      "",
    ),
    card_pay_way: toMerchantString(
      pickMerchantField(record, ["card_pay_way", "scheme"]),
      "",
    ),
    issuer: issuerCountry,
    issuerCountry: issuerCountry,
    issuer_country: issuerCountry,
    cardIssuerCode: issuerCountry,
    cardBelong: toMerchantString(
      pickMerchantField(record, ["card_belong", "issuer_country"]),
      issuerCountry,
    ),
    card_belong: toMerchantString(
      pickMerchantField(record, ["card_belong", "issuer_country"]),
      issuerCountry,
    ),
    netAmount: pickMerchantField(record, ["net_amount"], pickMerchantField(record, ["amount"], 0)),
    net_amount: pickMerchantField(record, ["net_amount"], pickMerchantField(record, ["amount"], 0)),
    refundableAmount: pickMerchantField(record, ["refundable_amount"], 0),
    serviceCharge: pickMerchantField(record, ["service_charge"], 0),
    clientTransactionId: toMerchantString(
      pickMerchantField(record, ["client_transaction_id"]),
      "",
    ),
    acquirerTransactionId: toMerchantString(
      pickMerchantField(record, ["acquirer_transaction_id"]),
      "",
    ),
    terminalId: toMerchantString(pickMerchantField(record, ["terminal_id"]), ""),
    terminalStan: toMerchantString(pickMerchantField(record, ["terminal_stan"]), ""),
    tradeNumber: toMerchantString(
      pickMerchantField(record, ["terminal_stan", "trade_number"]),
      "",
    ),
    createdAt: createdAt,
    created_at: createdAt,
    creationTime: createdAt,
    capturedTime: createdAt,
  };
}
